
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.tsx'
import './index.css'

// Import feature flags (initializes window.__featureFlags)
import './lib/featureFlags'

// Import production logger
import { prodLogger } from './utils/productionLogger'

// SEC-009: Initialize DOMPurify XSS protection (needed for security, run sync)
import { initializeDOMPurify } from './utils/sanitizeHTML'
initializeDOMPurify();

// PERF: Defer non-critical initializations to improve INP
// Use requestIdleCallback (with setTimeout fallback) to run after initial render
const deferInit = (fn: () => void) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 100);
  }
};

// Defer Sentry, PostHog, and version check to after initial render
deferInit(async () => {
  // PHASE 3: Initialize Sentry error monitoring
  const { initSentry } = await import('./utils/sentry');
  initSentry();

  // Initialize PostHog analytics
  const { initPostHog } = await import('./utils/analytics');
  initPostHog();

  // Initialize proactive version checking (auto-reload on new deployments)
  const { initVersionCheck } = await import('./utils/versionCheck');
  initVersionCheck();

  prodLogger.debug('📊 [PERF] Deferred initializations complete');
});

// Import matching algorithm for browser console testing (dev only, deferred)
if (import.meta.env.DEV) {
  deferInit(async () => {
    const { calculateSubsidyMatch, formatMatchResult } = await import('./services/subsidyMatcher');
    (window as any).calculateSubsidyMatch = calculateSubsidyMatch;
    (window as any).formatMatchResult = formatMatchResult;
    prodLogger.debug('🔬 [Dev Mode] Matching algorithm available at window.calculateSubsidyMatch');
  });
}

// Handle chunk loading errors (stale cache after deployment)
// When a new deployment happens, old cached HTML references old chunks that no longer exist
const RELOAD_KEY = 'chunk-load-retry';

window.addEventListener('error', (event) => {
  const error = event.error;
  const message = event.message || error?.message || '';

  // Detect chunk loading failures
  const isChunkLoadError =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    (message.includes('net::ERR_ABORTED') && message.includes('.js'));

  if (isChunkLoadError) {
    const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

    if (!hasReloaded) {
      // Mark that we've reloaded to prevent infinite loops
      sessionStorage.setItem(RELOAD_KEY, 'true');
      prodLogger.info('🔄 Chunk load error detected - reloading page for fresh assets');
      window.location.reload();
    } else {
      // Already reloaded once, clear flag and show error
      sessionStorage.removeItem(RELOAD_KEY);
      prodLogger.error('❌ Chunk load error persists after reload');
    }
  }
}, true);

// Clear reload flag on successful load
window.addEventListener('load', () => {
  sessionStorage.removeItem(RELOAD_KEY);
});

createRoot(document.getElementById("root")!).render(
  <BrowserRouter>
    <App />
  </BrowserRouter>
);
